/**
 * 演示环境专用的批量上传夹具（假数据）。
 *
 * 为什么要有它 —— 批量上传入口走的是 `<nz-upload>` 的原生文件选择框，
 * OS 弹窗只有真人点得动；智能体（`page_act` 只能点击 / 打字 / 滚动）没法给它
 * 塞一个文件。所以演示环境多一个「载入演示表格」按钮，点了把这里的行
 * 直接喂进和「解析真实表格」完全相同的那条链路：
 *
 *   夹具 → applyParsedTable() → verifyTableData()（服务端校验）→ 提交 → 任务列表
 *
 * 🔴 四个必须守住的坑（都踩过）：
 *
 * 1. 「优化目标」写字符串 `点击量`，**不要写数字 `0`**。
 *    服务端 `BatchUploadService::verifyTableDataService` 用 `empty()` 判必填，
 *    `0` 会被当成「没填」直接报错，而 `点击量` 恰好就是枚举值 0
 *    （`App\Constants\Field::optimize_objective`）。写中文让 `empty()` 过关，
 *    后端自己会翻成 0。见 `spotlight-backend/docs/demo-plan.md`。
 *
 * 2. 「创意笔记ID」必须是演示库里真实存在的笔记。
 *    ID 由 `App\Demo\DemoIdGenerator::noteId($i)` = `substr(md5('demo_note_' . $i), 0, 24)`
 *    生成，`EntitySeeder` 用它灌了 12 条进 `creative_note_association`。
 *    随手编一个 24 位串，创建那一步 `UtilService::getNoteList()` 就查不到这篇笔记。
 *    改演示数据（换种子 / 改 note 数量）时这里要一起换。
 *
 * 3. 「账户」写 `demo-旗舰店` —— 种子 5 个店（`demo-旗舰店 / 专卖店 / 官方店 /
 *    海外旗舰店 / 生活馆`）里的第一个，见 `EntitySeeder::seedAdvertisers()`。
 *    服务端按 `virtual_seller_name` 反查 account_id，写错就是「第 N 行账户不存在」。
 *
 * 4. 「信息流推广」的行**必须带「目标成本」**，搜索推广的行可以留空。
 *    执行阶段 `AutoExecuteJob` 有一道出价校验：产品种草 + `投放位置=信息流推广` +
 *    `竞价策略=手动出价`（枚举 1 + 2）时 `event_bid` 空着，整行就落成
 *    「执行失败：需要填写目标成本」；搜索推广不落在这个分支里，不带也能过。
 *    ⚠️ 校验那一步**不拦这个**（`verifyTableDataService` 只查必填列，而目标成本
 *    不是必填），所以提交时会一路「校验成功」，要等任务明细出来才看得见失败 ——
 *    别以为校验过了就没事。
 *    单位是**元**（后端 `event_bid * 100` 转分）。
 *
 * ⚠️ 列头（headers）必须和 `App\Constants\Field::CAMPAIGN_FIELD` 的中文键一字不差，
 *    值必须是该映射表里的中文枚举值。
 */

/** 一份「上传时解析出来的表格」：表头 + 数据行 */
export interface BatchUploadDemoFixture {
  /** 列名，顺序就是页面上铺开的顺序 */
  headers: string[];
  /** 数据行，键名与 headers 一致 */
  rows: Record<string, string | number>[];
}

/**
 * 按上传类型号索引的演示夹具（类型号对齐 `App\Constants\Task`）。
 * 目前只备了 `10`（添加计划）—— 演示动线第 3 幕走的就是它。
 * 别的类型要演示时，照着同样的口径补齐即可。
 */
export const DEMO_UPLOAD_FIXTURES: Record<number, BatchUploadDemoFixture> = {
  // 10 = 批量添加计划
  10: {
    headers: [
      '账户',
      '计划名称',
      '营销诉求',
      '投放位置',
      '优化目标',
      '竞价策略',
      '预算类型',
      '预算金额',
      '节假日预算上浮',
      '推广日期范围',
      '推广时段',
      '单元名称',
      '目标成本',
      '创意名称',
      '创意笔记ID',
    ],
    rows: [
      {
        账户: 'demo-旗舰店',
        计划名称: '表格-搜索-面膜测评合集',
        营销诉求: '产品种草',
        投放位置: '搜索推广',
        优化目标: '点击量',
        竞价策略: '手动出价',
        预算类型: '自定义',
        预算金额: 500,
        节假日预算上浮: '关闭',
        推广日期范围: '不限',
        推广时段: '不限',
        单元名称: '表格-单元-面膜测评',
        目标成本: '',
        创意名称: '表格-创意-面膜测评01',
        创意笔记ID: 'd4667d4f5bef26a56c2ce637',
      },
      {
        账户: 'demo-旗舰店',
        计划名称: '表格-信息流-通勤穿搭',
        营销诉求: '产品种草',
        投放位置: '信息流推广',
        优化目标: '点击量',
        竞价策略: '手动出价',
        预算类型: '自定义',
        预算金额: 500,
        节假日预算上浮: '关闭',
        推广日期范围: '不限',
        推广时段: '不限',
        单元名称: '表格-单元-通勤穿搭',
        目标成本: 3,
        创意名称: '表格-创意-通勤穿搭01',
        创意笔记ID: 'ed970a7c9aeb1ca1acc82833',
      },
      {
        账户: 'demo-旗舰店',
        计划名称: '表格-搜索-成分党必看',
        营销诉求: '产品种草',
        投放位置: '搜索推广',
        优化目标: '点击量',
        竞价策略: '手动出价',
        预算类型: '自定义',
        预算金额: 500,
        节假日预算上浮: '关闭',
        推广日期范围: '不限',
        推广时段: '不限',
        单元名称: '表格-单元-成分党',
        目标成本: '',
        创意名称: '表格-创意-成分党01',
        创意笔记ID: 'f0f2d9dcc386336ae88fbe6e',
      },
      {
        账户: 'demo-旗舰店',
        计划名称: '表格-信息流-秋冬保湿',
        营销诉求: '产品种草',
        投放位置: '信息流推广',
        优化目标: '点击量',
        竞价策略: '手动出价',
        预算类型: '自定义',
        预算金额: 500,
        节假日预算上浮: '关闭',
        推广日期范围: '不限',
        推广时段: '不限',
        单元名称: '表格-单元-秋冬保湿',
        目标成本: 3,
        创意名称: '表格-创意-秋冬保湿01',
        创意笔记ID: '5cc5da575e9e6c691d7f6401',
      },
      {
        账户: 'demo-旗舰店',
        计划名称: '表格-搜索-新手入门',
        营销诉求: '产品种草',
        投放位置: '搜索推广',
        优化目标: '点击量',
        竞价策略: '手动出价',
        预算类型: '自定义',
        预算金额: 500,
        节假日预算上浮: '关闭',
        推广日期范围: '不限',
        推广时段: '不限',
        单元名称: '表格-单元-新手入门',
        目标成本: '',
        创意名称: '表格-创意-新手入门01',
        创意笔记ID: 'b2cc296bd33bcd54a9898238',
      },
    ],
  },
};

/** 这个上传类型有没有备好的演示夹具 */
export function hasDemoFixture(type: number): boolean {
  return !!DEMO_UPLOAD_FIXTURES[type];
}

/** 取演示夹具；没有就返回 null */
export function getDemoFixture(type: number): BatchUploadDemoFixture | null {
  return DEMO_UPLOAD_FIXTURES[type] ?? null;
}
